import type {BymoPageProps} from '../../bymo-pages';
import {
    bymoPageGetInnerNavigatablePageById,
    DoppeDtoInnerPageKnownIds,
    DoppeDtoInnerPageType
} from '../../types/doppe-dto-inner-page';
import type {
    DoppeViewerPagesRoutesManager
} from '../../viewer-pages-routes-manager/doppe-viewer-pages-routes-manager';
import {DoppeViewerPageType} from '../doppe-viewer-pages';
import type {BymoPageContextProps} from '../../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {DoppeViewerLang} from '../../doppe-viewer/doppe-viewer-lang';
import {
    viewerListNavigationResolveProductsListTarget,
    viewerListNavigationResolveBlogPostsListTarget,
    viewerListNavigationResolveEventsListTarget,
    viewerListNavigationResolveServicesListTarget,
    viewerEntityNavigationResolveProductTarget,
    viewerEntityNavigationResolveBlogPostTarget,
    viewerEntityNavigationResolveEventTarget,
    viewerEntityNavigationResolveServiceTarget,
} from '../viewer-entity-navigation';
import {DoppeLinkSpecEntityType} from '../../types/doppe-action-cta-button';
import {DoppeDtoStoreProductType} from '../../types/doppe-dto-store-product';
import {DoppeDtoBookingServiceType} from '../../types/doppe-dto-booking-service';
import {DoppeDtoEventRegistrationType} from '../../types/doppe-dto-event';
import type {DoppeViewerRouterClientService} from '../../../client/doppe-viewer-app-client-services/doppe-viewer-router-client-service';
import {bymoPageContextPropsResolveEntityInfo} from '../../../doppe-sdk/bymo-page/bymo-page-context-props';

/**
 * Resolves a page ID to a URL path string, handling the Home page special case
 * and respecting vertical list page settings for internal vs external routing.
 *
 * @returns The resolved URL path, or null if the page cannot be resolved.
 */
export function viewerPageNavigationResolvePageRoute(
    pageId: string,
    options: {
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
        bymoPageContextProps: BymoPageContextProps;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
    }
): string | null {
    const {
        bymoPageProps,
        viewerPagesRoutesManager,
        bymoPageContextProps,
        viewerLang,
        showMobileView = false,
    } = options;

    if (pageId === DoppeDtoInnerPageKnownIds.Home) {
        return viewerPagesRoutesManager.homePage();
    }

    const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
    if (!page) {
        return null;
    }

    const listNavigationOptions = {
        bymoPageContextProps: bymoPageContextProps,
        viewerLang: viewerLang,
        showMobileView: showMobileView,
    };

    switch (page.type) {
        case DoppeDtoInnerPageType.Products: {
            const result = viewerListNavigationResolveProductsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                return viewerPagesRoutesManager.resolvePath({
                    pageType: DoppeViewerPageType.ProductsList
                });
            }
            return result.externalUrl ?? null;
        }
        case DoppeDtoInnerPageType.BlogPosts: {
            const result = viewerListNavigationResolveBlogPostsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                return viewerPagesRoutesManager.resolvePath({
                    pageType: DoppeViewerPageType.BlogPostsList
                });
            }
            return result.externalUrl ?? null;
        }
        case DoppeDtoInnerPageType.Events: {
            const result = viewerListNavigationResolveEventsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                return viewerPagesRoutesManager.resolvePath({
                    pageType: DoppeViewerPageType.EventsList
                });
            }
            return result.externalUrl ?? null;
        }
        case DoppeDtoInnerPageType.Services: {
            const result = viewerListNavigationResolveServicesListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                return viewerPagesRoutesManager.resolvePath({
                    pageType: DoppeViewerPageType.ServicesList
                });
            }
            return result.externalUrl ?? null;
        }
        case DoppeDtoInnerPageType.Cart: {
            return viewerPagesRoutesManager.cartPage();
        }
        case DoppeDtoInnerPageType.Content:
        default:
            return viewerPagesRoutesManager.contentPage({slug: page.slug});
    }
}

/**
 * Navigates to a page by ID, handling the Home page special case
 * and respecting vertical list page settings for internal vs external routing.
 */
export function viewerPageNavigationNavigateToPage(
    pageId: string,
    options: {
        router: DoppeViewerRouterClientService;
        bymoPageProps: BymoPageProps;
        bymoPageContextProps: BymoPageContextProps;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
        isLivePreviewMode?: boolean;
        handleExternalUrl: (url: string) => void;
    }
): void {
    const {
        router,
        bymoPageProps,
        bymoPageContextProps,
        viewerLang,
        showMobileView = false,
        isLivePreviewMode = false,
        handleExternalUrl,
    } = options;

    if (pageId === DoppeDtoInnerPageKnownIds.Home) {
        router.navigateToHome();
        return;
    }

    const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
    if (!page) {
        return;
    }

    const listNavigationOptions = {
        bymoPageContextProps: bymoPageContextProps,
        viewerLang: viewerLang,
        showMobileView: showMobileView,
        isLivePreviewMode: isLivePreviewMode,
    };

    switch (page.type) {
        case DoppeDtoInnerPageType.Products: {
            const result = viewerListNavigationResolveProductsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                router.navigateToProductsList();
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeDtoInnerPageType.BlogPosts: {
            const result = viewerListNavigationResolveBlogPostsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                router.navigateToBlogPostsList();
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeDtoInnerPageType.Events: {
            const result = viewerListNavigationResolveEventsListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                router.navigateToEventsList();
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeDtoInnerPageType.Services: {
            const result = viewerListNavigationResolveServicesListTarget(listNavigationOptions);
            if (result.useInternalRouting) {
                router.navigateToServicesList();
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeDtoInnerPageType.Cart: {
            router.navigateToCart();
            break;
        }
        case DoppeDtoInnerPageType.Content:
        default:
            router.navigateToContentPage(page.slug);
            break;
    }
}

/**
 * Navigates to an entity item page by type and ID, handling internal vs external routing
 * and entity sub-type routing (e.g., digital product vs physical product).
 */
export function viewerPageNavigationNavigateToEntity(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    options: {
        router: DoppeViewerRouterClientService;
        bymoPageContextProps: BymoPageContextProps;
        viewerLang: DoppeViewerLang;
        showMobileView?: boolean;
        isLivePreviewMode?: boolean;
        handleExternalUrl: (url: string) => void;
    }
): void {
    const {
        router,
        bymoPageContextProps,
        viewerLang,
        showMobileView = false,
        isLivePreviewMode = false,
        handleExternalUrl,
    } = options;

    const entityInfo = bymoPageContextPropsResolveEntityInfo(
        bymoPageContextProps,
        entityType,
        entityId
    );
    const slug = entityInfo.slug;

    const navigationOptions = {
        bymoPageContextProps: bymoPageContextProps,
        viewerLang: viewerLang,
        showMobileView: showMobileView,
        isLivePreviewMode: isLivePreviewMode,
    };

    switch (entityType) {
        case DoppeLinkSpecEntityType.Product: {
            const result = viewerEntityNavigationResolveProductTarget(
                {slug: slug, productPageUrl: entityInfo.pageUrl},
                navigationOptions
            );
            if (result.useInternalRouting && result.slug) {
                if (entityInfo.innerType === DoppeDtoStoreProductType.Digital) {
                    router.navigateToDigitalProductItem(result.slug);
                } else {
                    router.navigateToProductItem(result.slug);
                }
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeLinkSpecEntityType.BlogPost: {
            const result = viewerEntityNavigationResolveBlogPostTarget(
                {slug: slug, pageUrl: entityInfo.pageUrl},
                navigationOptions
            );
            if (result.useInternalRouting && result.slug) {
                router.navigateToBlogPostItem(result.slug);
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeLinkSpecEntityType.Event: {
            const result = viewerEntityNavigationResolveEventTarget(
                {slug: slug, pageUrl: entityInfo.pageUrl},
                navigationOptions
            );
            if (result.useInternalRouting && result.slug) {
                if (entityInfo.innerType === DoppeDtoEventRegistrationType.Ticketing) {
                    router.navigateToTicketingEventItem(result.slug);
                } else {
                    router.navigateToEventItem(result.slug);
                }
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
        case DoppeLinkSpecEntityType.Service: {
            const result = viewerEntityNavigationResolveServiceTarget(
                {slug: slug, servicePageUrl: entityInfo.pageUrl},
                navigationOptions
            );
            if (result.useInternalRouting && result.slug) {
                switch (entityInfo.innerType) {
                    case DoppeDtoBookingServiceType.COURSE:
                        router.navigateToCourseItem(result.slug);
                        break;
                    case DoppeDtoBookingServiceType.Class:
                        router.navigateToClassItem(result.slug);
                        break;
                    case DoppeDtoBookingServiceType.APPOINTMENT:
                    default:
                        router.navigateToAppointmentItem(result.slug);
                        break;
                }
            } else if (result.externalUrl) {
                handleExternalUrl(result.externalUrl);
            }
            break;
        }
    }
}
